/**
 * EpBuyPage — 求購大廳
 *
 * 4 子視圖：
 *   1) 求購大廳    — GET /api/ep/buy-orders
 *   2) 我的求購    — GET /api/ep/buy-orders?mine=true
 *   3) 我的賣出    — GET /api/ep/sell-records
 *   4) 問題訂單    — GET /api/ep/buy-orders?status=dispute
 *
 * 右上角「添加」按鈕 → opens create-buy-order sheet.
 */
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { epAPI } from '../api/ep';
import { useAuthStore } from '../store/auth';
import { Button } from '../components/ui/Button';
import { ModalSheet } from '../components/ui/ModalSheet';
import { EmptyState } from '../components/ui/EmptyState';
import { LoadingSkeleton } from '../components/ui/LoadingSkeleton';
import { ThemedPageHeader } from '../components/ui/ThemedPageHeader';
import { ThemedFixedTopBar, ThemedInput, ThemedField, ThemedAvatar, ThemedStatusBadge, ThemedPanel } from '../components/ui/ThemedSurface';
import { EPBUY_COLORS as C, TRADING_COLORS } from '@/constants';
import { getEpStatusLabel } from '../utils/ep-status';

/* ------------------------------------------------------------------ types */

interface EpBuyOrder {
  id: string;
  seller_id: number;
  buyer_id?: number | null;
  buyer_name?: string;
  buyer_avatar?: string | null;
  amount: string;
  unit_price: string;
  status: string;
  create_time: string;
}

interface EpSellRecord {
  id: string;
  buyer_name?: string;
  amount: string;
  unit_price: string;
  status: string;
  create_time: string;
}

interface ListResp<T> {
  total: number;
  rows: T[];
}

/* --------------------------------------------------------------- constants */
type TabKey = 'hall' | 'my-buy' | 'my-sell' | 'dispute';

const EP_BUY_TABS: { key: TabKey; labelKey: string }[] = [
  { key: 'hall', labelKey: 'epBuy.hall' },
  { key: 'my-buy', labelKey: 'epBuy.myBuy' },
  { key: 'my-sell', labelKey: 'epBuy.mySell' },
  { key: 'dispute', labelKey: 'epBuy.dispute' },
];

function fmt(v: string, digits = 4): string {
  const n = Number(v);
  if (!Number.isFinite(n)) return v;
  return n.toLocaleString(undefined, { maximumFractionDigits: digits });
}

/* ---------------------------------------------------------- AvatarCircle */



function TabStrip({ tabs, value, onChange }: { tabs: { key: TabKey; label: string }[]; value: TabKey; onChange: (value: TabKey) => void }) {
  return (
    <div className="grid grid-cols-4 gap-1">
      {tabs.map((tab) => {
        const active = tab.key === value;
        return (
          <button
            key={tab.key}
            type="button"
            onClick={() => onChange(tab.key)}
            className="relative h-9 overflow-hidden px-1 text-[11px] font-semibold transition-all active:scale-[0.98]"
            style={{
              color: active ? C.gold : C.muted,
            }}
          >
            {active && <span className="absolute inset-x-3 bottom-0 h-px" style={{ background: C.gold }} />}
            <span className="relative block truncate">{tab.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function FixedTabStrip({ tabs, value, onChange }: { tabs: { key: TabKey; label: string }[]; value: TabKey; onChange: (value: TabKey) => void }) {
  return (
    <ThemedFixedTopBar background={TRADING_COLORS.fixedTopBg} className="py-1">
      <TabStrip tabs={tabs} value={value} onChange={onChange} />
    </ThemedFixedTopBar>
  );
}

function HallFilter() {
  const { t } = useTranslation();

  return (
    <div className="fixed left-1/2 top-[110px] z-30 w-full max-w-[430px] -translate-x-1/2 px-5 pb-3" style={{ background: C.topbarBg }}>
      <div className="relative overflow-hidden">
        <div className="flex items-center gap-2 text-xs" style={{ color: C.muted }}>
          <span>{t('epBuy.filter')}</span>
          <span>|</span>
          <span className="rounded-lg border px-3 py-1 text-xs" style={{ borderColor: C.softBorder, background: C.chipBg, color: C.text }}>
            {t('common.all')} &gt;
          </span>
        </div>
      </div>
    </div>
  );
}

function LoadingCards() {
  return (
    <>
      <LoadingSkeleton className="h-20" />
      <LoadingSkeleton className="h-20" />
      <LoadingSkeleton className="h-20" />
    </>
  );
}

function BuyOrderList({ orders, emptyTitle }: { orders?: EpBuyOrder[]; emptyTitle: string }) {
  const rows = orders ?? [];

  if (rows.length === 0) return <EmptyState title={emptyTitle} />;

  return (
    <>
      {rows.map((order) => <BuyOrderCard key={order.id} order={order} />)}
    </>
  );
}

function SellRecordList({ records, emptyTitle }: { records?: EpSellRecord[]; emptyTitle: string }) {
  const rows = records ?? [];

  if (rows.length === 0) return <EmptyState title={emptyTitle} />;

  return (
    <>
      {rows.map((record) => <SellRecordCard key={record.id} record={record} />)}
    </>
  );
}



/* ----------------------------------------------------------------- Cards */

function BuyOrderCard({ order }: { order: EpBuyOrder }) {
  const { t } = useTranslation();
  return (
    <ThemedPanel className="p-0" theme={C}>
      <div className="flex items-center gap-3 px-4 py-3">
      <ThemedAvatar src={order.buyer_avatar} name={order.buyer_name} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="truncate text-sm font-semibold" style={{ color: C.text }}>
            {order.buyer_name ??
              (order.buyer_id != null ? `#${order.buyer_id}` : `卖家 #${order.seller_id}`)}
          </span>
          <ThemedStatusBadge label={getEpStatusLabel(order.status, t)} />
        </div>
        <div className="mt-0.5 flex items-center gap-3 text-xs" style={{ color: C.muted }}>
          <span>{t('common.amount', '數量')}: <span className="font-mono" style={{ color: C.text }}>{fmt(order.amount)}</span></span>
            <span>{t('common.price', '單價')}: <span className="font-mono" style={{ color: C.gold }}>{fmt(order.unit_price)}</span></span>
        </div>
        <div className="mt-0.5 text-xs" style={{ color: C.muted }}>{order.create_time}</div>
      </div>
      </div>
    </ThemedPanel>
  );
}

function SellRecordCard({ record }: { record: EpSellRecord }) {
  const { t } = useTranslation();
  return (
    <ThemedPanel className="p-0" theme={C}>
      <div className="px-4 py-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold" style={{ color: C.text }}>#{record.id}</span>
          <ThemedStatusBadge label={getEpStatusLabel(record.status, t)} />
        </div>
        <div className="mt-1 flex items-center gap-3 text-xs" style={{ color: C.muted }}>
          <span>{t('common.amount', '數量')}: <span className="font-mono" style={{ color: C.text }}>{fmt(record.amount)}</span></span>
          <span>{t('common.price', '單價')}: <span className="font-mono" style={{ color: C.gold }}>{fmt(record.unit_price)}</span></span>
        </div>
        <div className="mt-0.5 text-xs" style={{ color: C.muted }}>{record.create_time}</div>
      </div>
    </ThemedPanel>
  );
}

/* ------------------------------------------------------- CreateBuySheet */

interface CreateBuySheetProps {
  epType: string;
  onClose: () => void;
  onSuccess: () => void;
}

function CreateBuySheet({ epType, onClose, onSuccess }: CreateBuySheetProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <ModalSheet open onClose={onClose} title={t('epBuy.addBuyOrder', '添加求購')}>
      <p className="text-sm leading-relaxed" style={{ color: C.muted }}>
        {t(
          'epBuy.claimHint',
          '求购 = 认领大厅中的 EP 卖单（POST /api/ep/orders/claim）。请在大厅选择卖单接盘；买家收到 EP1/EP2（与大厅类型一致）。',
        )}
      </p>
      <div className="grid grid-cols-2 gap-3 pt-4">
        <Button type="button" variant="secondary" onClick={onClose} className="rounded-full border bg-black/25 text-white/80" style={{ borderColor: C.softBorder }}>
          {t('common.cancel', '取消')}
        </Button>
        <Button
          type="button"
          onClick={() => {
            onSuccess();
            navigate(`/ep-trading${epType === 'EP2' ? '?type=ep2' : ''}`);
          }}
          className="rounded-xl border text-sm font-medium"
          style={{ background: C.buttonBg, borderColor: C.buttonBorder, color: C.text }}
        >
          {t('epBuy.goHall', '前往交易大厅')}
        </Button>
      </div>
    </ModalSheet>
  );
}

/* =================================================================== Page */

export function EpBuyPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const epType = searchParams.get('type') === 'ep2' ? 'EP2' : 'EP1';
  const userId = useAuthStore((s) => s.profile?.id);

  const [activeTab, setActiveTab] = useState<TabKey>('hall');
  const [showCreate, setShowCreate] = useState(false);

  const tabs = EP_BUY_TABS.map((tab) => ({ ...tab, label: t(tab.labelKey) }));

  /* ---- hall ---- */
  const epTypeKey = epType === 'EP2' ? 'ep2' : 'ep1';

  const { data: hallOrders, isLoading: loadingHall } = useQuery({
    queryKey: ['ep', 'orders', 'hall-buy', epTypeKey],
    queryFn: () => epAPI.listOrders({ status: 'open', ep_type: epTypeKey, page: 1, limit: 50 }),
    enabled: activeTab === 'hall',
    refetchInterval: 10_000,
  });

  const { data: myBuyOrders, isLoading: loadingMyBuy } = useQuery({
    queryKey: ['ep', 'orders', 'buyer', userId],
    queryFn: () => epAPI.listOrders({ buyer_id: userId!, page: 1, limit: 50 }),
    enabled: activeTab === 'my-buy' && typeof userId === 'number',
    refetchInterval: 10_000,
  });

  const { data: mySellRecords, isLoading: loadingMySell } = useQuery({
    queryKey: ['ep', 'orders', 'seller', userId],
    queryFn: () => epAPI.listOrders({ seller_id: userId!, page: 1, limit: 50 }),
    enabled: activeTab === 'my-sell' && typeof userId === 'number',
    refetchInterval: 10_000,
  });

  const { data: disputeOrders, isLoading: loadingDispute } = useQuery({
    queryKey: ['ep', 'orders', 'disputed'],
    queryFn: () => epAPI.listOrders({ status: 'disputed', page: 1, limit: 50 }),
    enabled: activeTab === 'dispute',
    refetchInterval: 10_000,
  });

  const isLoading =
    (activeTab === 'hall' && loadingHall) ||
    (activeTab === 'my-buy' && loadingMyBuy) ||
    (activeTab === 'my-sell' && loadingMySell) ||
    (activeTab === 'dispute' && loadingDispute);

  return (
    <div className="relative min-h-full pb-8" style={{ background: C.pageBg }}>
      <div className="relative mx-auto min-h-full w-full max-w-[430px]">
        <ThemedPageHeader title={t('epBuy.pageTitle', '求購')} action={t('epBuy.add', '添加')} onBack={() => navigate(-1)} onAction={() => setShowCreate(true)} theme={C} />

        <FixedTabStrip tabs={tabs} value={activeTab} onChange={setActiveTab} />
        {activeTab === 'hall' && <HallFilter />}

        <div className={`space-y-2 px-5 ${activeTab === 'hall' ? 'pt-[148px]' : 'pt-[112px]'}`}>
          {isLoading && <LoadingCards />}
          {activeTab === 'hall' && !loadingHall && (
            <BuyOrderList orders={hallOrders?.rows} emptyTitle={t('epBuy.noBuyOrders')} />
          )}
          {activeTab === 'my-buy' && !loadingMyBuy && (
            <BuyOrderList orders={myBuyOrders?.rows} emptyTitle={t('epBuy.noBuyOrders')} />
          )}
          {activeTab === 'my-sell' && !loadingMySell && (
            <SellRecordList records={mySellRecords?.rows} emptyTitle={t('epBuy.noSellRecords')} />
          )}
          {activeTab === 'dispute' && !loadingDispute && (
            <BuyOrderList orders={disputeOrders?.rows} emptyTitle={t('epBuy.noDispute')} />
          )}
        </div>

      </div>

      {/* ---- Create Sheet ---- */}
      {showCreate && (
        <CreateBuySheet
          epType={epType}
          onClose={() => setShowCreate(false)}
          onSuccess={() => {
            setShowCreate(false);
            setActiveTab('my-buy');
          }}
        />
      )}
    </div>
  );
}

export default EpBuyPage;
